/**
 * C1 权限模型 · 契约层（2026-10-06 用户五条口径）
 *
 * 用户原话（`docs/plan/permission-model-design.md` 有全文）：
 *  1. 权限分**店铺权限**与**功能权限**；**有店铺权限才能看到该店铺的数据**；
 *  2. **模块权限按模块显示，分三档：无权限（看不到）/ 写入 / 只读**；
 *  3. 管理员**只能看见自己这个分支**（自己创建的、以及自己创建的管理员再创建的）；
 *     **只能把自己有的权限**分配给自己创建的用户；第一版不做可编辑可见范围；
 *  4. **`admin` 是超级管理员，不受任何限制**；5. **权限要细分到每个功能**。
 *
 * 本文件只做**纯判定**：不碰数据库、HTTP、环境变量（契约层红线）。
 * 层级清单 `FEATURE_POINTS` 是**唯一真相**：后台渲染、前端门禁、API 中间件都用它，
 * 不允许在别处再列一份（本仓库为"两份清单分叉"栽过）。
 */

// ---------------------------------------------------------------------------
// 1. 三档权限
// ---------------------------------------------------------------------------

/** `无权限（看不到） / 只读 / 写入`。数值用于比较（**只能授出 ≤ 自己**的档位）。 */
export type AccessLevel = "none" | "read" | "write";

/** 档位强弱（`none < read < write`）。**唯一比较依据**，不要在别处再写一套顺序。 */
export const ACCESS_RANK: Readonly<Record<AccessLevel, number>> = { none: 0, read: 1, write: 2 };

/** 是否 ≥ `min`（例如 `atLeast(x, "write")` = 可写）。 */
export function atLeast(level: AccessLevel, min: AccessLevel): boolean {
  return ACCESS_RANK[level] >= ACCESS_RANK[min];
}

/** 两档取高者（用于"多来源授权取并集"）。 */
export function highestAccess(a: AccessLevel, b: AccessLevel): AccessLevel {
  return ACCESS_RANK[a] >= ACCESS_RANK[b] ? a : b;
}

// ---------------------------------------------------------------------------
// 2. 功能点清单（**唯一真相**；"细分到每个功能"）
// ---------------------------------------------------------------------------

/** 功能点所属模块（= 导航分区 + 设置）。 */
export type FeatureModule = "经营" | "广告" | "商品" | "数据" | "设置";

export interface FeaturePoint {
  readonly key: string;
  readonly label: string;
  readonly module: FeatureModule;
  /** 该功能点**有无写动作**：`false` = 只能"无权限/只读"（写入档对它无意义，UI 不应提供）。 */
  readonly writable: boolean;
}

/**
 * 全部功能点。**新增页面/写动作必须在这里登记**，否则中间件按 fail-closed 直接拒（这是有意的）。
 */
/**
 * ⚠️ `writable` 必须与**实际注册的端点**一致：
 *  guard 测试会断言"`required: write` 的路由，其功能点必须 `writable: true`"——
 *  2026-10-06 就是这条抓出了「货号看板标成只读、但它有 4 个分组增删改端点」的不一致
 *  （`canWrite` 对 `writable:false` **连超管都拒** ⇒ 那 4 个端点会被永久 403）。
 */
export const FEATURE_POINTS: readonly FeaturePoint[] = [
  { key: "overview", label: "经营概览", module: "经营", writable: false },
  { key: "spend_overview", label: "花费总览", module: "经营", writable: false },
  { key: "ad_metrics", label: "广告指标表", module: "广告", writable: true },
  { key: "ad_management", label: "广告管理", module: "广告", writable: true },
  { key: "ad_management_merge", label: "广告管理 · 并入", module: "广告", writable: true },
  { key: "style_code_board", label: "货号看板", module: "广告", writable: true },
  { key: "creative_board", label: "素材看板", module: "广告", writable: true },
  { key: "google_campaigns", label: "Google 系列", module: "广告", writable: true },
  { key: "auto_control", label: "自动启停（含真执行）", module: "广告", writable: true },
  { key: "auto_budget", label: "自动预算（含真执行）", module: "广告", writable: true },
  { key: "products", label: "商品管理", module: "商品", writable: true },
  { key: "sync", label: "数据同步", module: "数据", writable: true },
  { key: "settings_accounts", label: "设置 · 账号", module: "设置", writable: true },
  { key: "settings_brands_shops", label: "设置 · 品牌与店铺", module: "设置", writable: true },
  { key: "settings_budget_ratio", label: "设置 · 预算占比", module: "设置", writable: true },
  { key: "settings_compliance", label: "设置 · 合规体检", module: "设置", writable: false },
  { key: "settings_display", label: "设置 · 显示与数据源", module: "设置", writable: true },
  // 2026-10-28（用户：「权限要能分配给其他人，不能只有超级管理员才能填」）：
  // Meta 凭证回填（个人令牌 / 系统用户令牌）⇒ **走本功能点**，可被授予任意角色/用户；
  // 超级管理员照旧隐式全权（`features={}` 约定），但**不再唯一**。
  { key: "settings_credentials", label: "设置 · 凭证与令牌", module: "设置", writable: true },
  { key: "settings_permissions", label: "设置 · 权限管理", module: "设置", writable: true },
] as const;

/** 功能点 key 的联合类型（登记表推导，**不要手写第二份**）。 */
export type FeatureKey = (typeof FEATURE_POINTS)[number]["key"];

const FEATURE_BY_KEY: ReadonlyMap<string, FeaturePoint> = new Map(
  FEATURE_POINTS.map((point) => [point.key, point]),
);

/** 已登记的功能点（未登记 ⇒ `undefined` ⇒ 调用方必须 fail closed）。 */
export function featurePointOf(key: string): FeaturePoint | undefined {
  return FEATURE_BY_KEY.get(key);
}

// ---------------------------------------------------------------------------
// 3. 授权数据与判定（**fail closed**）
// ---------------------------------------------------------------------------

/** 一个用户的**功能权限**：缺键 = 无权限（**不要**用显式的 `none` 当"没配"）。 */
export type FeatureGrants = Readonly<Partial<Record<FeatureKey, AccessLevel>>>;

/** 一个用户的**店铺权限** = 店铺 id 集合。 */
export type ShopGrants = readonly string[];

export interface UserAccess {
  /** `admin` = 超级管理员：**不受任何限制**（不参与判定、不查 grants）。 */
  readonly role: string;
  readonly shopIds: ShopGrants;
  readonly features: FeatureGrants;
}

/** 是否超级管理员（用户口径 4）。 */
export function isSuperAdmin(user: Pick<UserAccess, "role">): boolean {
  return user.role === "admin";
}

/**
 * 某功能点的**有效档位**。
 * ⚠️ **fail closed**：未登记的功能点、缺键、非法档位 ⇒ `none`（绝不默认放行）。
 */
export function accessOf(user: Pick<UserAccess, "role" | "features">, key: string): AccessLevel {
  if (isSuperAdmin(user)) return "write";
  if (featurePointOf(key) === undefined) return "none";
  const level = user.features[key as FeatureKey];
  if (level === undefined) return "none";
  return level === "read" || level === "write" ? level : "none";
}

/** 能不能**看到**该功能（`read` 或 `write`）。 */
export function canView(user: Pick<UserAccess, "role" | "features">, key: string): boolean {
  return atLeast(accessOf(user, key), "read");
}

/**
 * 能不能在该功能**写入**。
 * ⚠️ 对 `writable: false` 的功能点**一律 false**（即使授了 `write`）—— 只读页面没有写动作，
 * 授出 `write` 说明配置错了，这里不制造"看不见的写权限"。
 */
export function canWrite(user: Pick<UserAccess, "role" | "features">, key: string): boolean {
  const point = featurePointOf(key);
  if (point === undefined || !point.writable) return false;
  return atLeast(accessOf(user, key), "write");
}

/**
 * 该用户**可见的店铺集合**。
 * ⚠️ 无 grants ⇒ **空集**（fail closed：什么都看不到），**不是**"全部可见"。
 */
export function visibleShopIds(user: Pick<UserAccess, "role" | "shopIds">): readonly string[] {
  if (isSuperAdmin(user)) return user.shopIds; // 超管：由调用方传全量店铺（不受限制）
  return [...new Set(user.shopIds)].sort();
}

/** 该用户能否看某店铺的数据（**店铺权限是能看到数据的唯一前提**）。 */
export function canSeeShop(user: Pick<UserAccess, "role" | "shopIds">, shopId: string): boolean {
  if (isSuperAdmin(user)) return true;
  return user.shopIds.includes(shopId);
}

// ---------------------------------------------------------------------------
// 4. 授权约束：**管理员只能授出自己有的**（用户口径 3）
// ---------------------------------------------------------------------------

export type GrantRejectionCode =
  /** 授出的店铺不在自己可见范围内 */
  | "shop_not_owned"
  /** 授出的功能档位高于自己的档位 */
  | "feature_exceeds_own"
  /** 授出的功能点未登记 */
  | "unknown_feature"
  /** 给只读功能点授了写权限（配置错误） */
  | "write_on_readonly_feature";

export interface GrantRejection {
  readonly code: GrantRejectionCode;
  /** 人类可读（后台要直接显示给管理员看，不要只给码）。 */
  readonly message: string;
  /** 涉及的对象（店铺 id / 功能 key）。 */
  readonly subject: string;
}

export interface GrantDecision {
  readonly ok: boolean;
  readonly rejections: readonly GrantRejection[];
}

/**
 * 校验一次授权是否合法（**服务端必须调用**；前端隐藏按钮只是体验，不是安全）。
 *
 * `actor` 是**发起授权的管理员**（超管：不受限）；`target` 是被授予的内容。
 */
export function validateGrantAssignment(input: {
  readonly actor: Pick<UserAccess, "role" | "shopIds" | "features">;
  readonly shops: readonly string[];
  readonly features: FeatureGrants;
}): GrantDecision {
  const rejections: GrantRejection[] = [];
  const superAdmin = isSuperAdmin(input.actor);

  if (!superAdmin) {
    const owned = new Set(input.actor.shopIds);
    for (const shopId of input.shops) {
      if (!owned.has(shopId)) {
        rejections.push({
          code: "shop_not_owned",
          message: `店铺 ${shopId} 不在你的权限范围内，不能授予`,
          subject: shopId,
        });
      }
    }
  }

  for (const [key, level] of Object.entries(input.features)) {
    const point = featurePointOf(key);
    if (point === undefined) {
      rejections.push({ code: "unknown_feature", message: `未登记的功能点 ${key}`, subject: key });
      continue;
    }
    if (level === undefined) continue;
    if (level === "write" && !point.writable) {
      rejections.push({
        code: "write_on_readonly_feature",
        message: `${point.label} 只读，不能授予写权限`,
        subject: key,
      });
    }
    if (!superAdmin && !atLeast(input.actor.features[key as FeatureKey] ?? "none", level)) {
      rejections.push({
        code: "feature_exceeds_own",
        message: `${point.label} 的权限不能高于你自己的权限`,
        subject: key,
      });
    }
  }

  return { ok: rejections.length === 0, rejections };
}

/**
 * 分支可见性（用户口径 3）的**纯判定**：`actor` 能否管理 `target`。
 * 数据层负责给出"谁是自己的后代"（物化路径/递归 CTE），契约只负责逻辑：
 *  · 超管：都能；· 自己不能管自己；· 只有 target 在自己分支内才可以。
 */
export function canManageUser(
  actor: Pick<UserAccess, "role">,
  input: { readonly actorUserId: string; readonly targetUserId: string; readonly targetInOwnBranch: boolean },
): boolean {
  if (isSuperAdmin(actor)) return input.actorUserId !== input.targetUserId;
  if (input.actorUserId === input.targetUserId) return false;
  return input.targetInOwnBranch;
}
